import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useAppSelector, useAppDispatch } from "@/core/app/hooks"
import { useParams } from "react-router-dom"
import { RootState } from "@/core/app/store"
import Scans from "@/modules/Records/Scans/Scans2"
import { PhotosAndXRays } from "@/UDTreat/left/PhotosAndXRaysForCreatingProcess"
import { setWidthAll,  setIsCreateSelfplanRefinement, setIsCreateNewRefinement, setStageShowType, setIsDoingPresetup } from "@/UDTreat/udTreatSlice"
import CloseIcon from "@mui/icons-material/Close"
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import { closeAlert, setAlert } from "@/core/app/slices/alert/alertSlice"
import { UAlert, UButton, UText } from "@/components/index"
import { Box, Button, IconButton } from "@mui/material"
import { caseManagement, resourcesSynchronization, wasmModule, treatView, preview} from "@/gluelayer"
import { uploadCaseZips } from "@/core/app/slices/clinical/clinicalThunkApi"
import { setUploadStls } from "../../core/app/slices/records/scans/scansSlice"
import { setPhotoTypeJson } from "../../core/app/slices/records/photograph/photographSlice"
import DialogBox from "@/modules/Clinical/components/DialogBox/dialogBox"
import { setRefinementReady, setOpenMode } from "@/core/app/slices/clinical/clinicalSlice"
import { cancelRefine } from "@/gluelayer/core/modules/sagemaker/presetup"
import { resourceManager } from "@/gluelayer/core/modules/resourcemanager/resourcemgr"
import { getFileZips } from "@/core/app/slices/clinical/clinical.service"

function getFileExtension(file: File) {
  const parts = file.name.split(".")
  const extension = parts[parts.length - 1].toLowerCase()
  return extension
}

export const CreateSelfplanRefinement = ({ requestRefinementPresetup, selfplanSubmit, handleCancel, setIsCancelRefine, setSelfplanRefineAiSuccess, showJsPreview }) => {
  const { refinementNum, widthAll } = useAppSelector(
    (state: RootState) => state.udTreatService,
  )
  const { current_orgId } = useAppSelector(
    (state: RootState) => state.userService.user,
  )
  const dispatch = useAppDispatch()
  const { t } = useTranslation("common")
  const [step, setStep] = useState(0)
  const upperModelCache = useRef(null)
  const lowerModelCache = useRef(null)
  const { caseDetail } = useAppSelector((state: RootState) => state.caseService)
  const { uploadSTL } = useAppSelector((state) => state.scansService)
  const { patientId, caseId } = useParams()
  const [cancelWarning, setCancelWarning] = useState(false)
  const { alertMsg, isError, btnText, btnAction, timeout } = useAppSelector(
      (state: RootState) => state.alert,
    )

  const getCurrentStepComponent = () => {
    if (step === 0) {
      return <Scans isRefinement={true}/>
    } else if (step === 1) {
      return (
        <PhotosAndXRays
          isZoomoutAbled={true}
          isDragAbled={true}
          isDeleteAbled={true}
          refinementIndex={refinementNum}
        />
      )
    }
  }
 
  const changeToNextStep = async () => {
    dispatch(closeAlert())
    if (step === 0) {
      const udesignCategory = caseDetail?.udesign_category? caseDetail.udesign_category : `"A"/"A"`
      const arches = udesignCategory.split("/")
      const isupper = arches[0].includes("R") || arches[0].includes("A");
      const islower = arches[1].includes("R") || arches[1].includes("A");
      if (isupper && islower && !uploadSTL.upper && !uploadSTL.lower) {
        dispatch(
          setAlert({
            message: t("records.scans.noScanFile"),
            isError: true,
          }),
        )
        return
      } else if (isupper && !uploadSTL.upper){
        dispatch(
          setAlert({
            message: t("records.scans.missUpperScan"),
            isError: true,
          }),
        )
        return
      }else if (islower && !uploadSTL.lower){
        dispatch(
          setAlert({
            message: t("records.scans.missLowerScan"),
            isError: true,
          }),
        )
        return
      }
      // check if need to save
      let needResetPresetup = false
      await new Promise(resolve => {
        setTimeout(async () => {
          if (upperModelCache.current == uploadSTL.upper && lowerModelCache.current == uploadSTL.lower) {
            resolve()
            return
          }
          const mtcNames = [
            "/test/case/Setting Data0/arch_u.mtc",
            "/test/case/Setting Data0/arch_l.mtc",
          ]
          const mtcFiles = resourcesSynchronization.getFilesByPathname([
            mtcNames[0],
            mtcNames[1],
          ])
          if (mtcFiles[0] === undefined && mtcFiles[1] === undefined){
            await getFileZips({ orgId: current_orgId, patientId, caseId, zipNames: ["bulk0"] }).then(async (r) => {
              const zips = new Map<string, Blob>()
              if (r) {
                zips.set("bulk0", r[0].data)
              }
              const filelist = await resourceManager.turnBlobMapIntoFileDirectory(
                zips,
              )
              const archU = filelist.bulk0["Setting Data0/arch_u.mtc"]
              const archL = filelist.bulk0["Setting Data0/arch_l.mtc"]
              await resourcesSynchronization.saveFileToZip(archU,'arch_u.mtc','Setting Data0')
              await resourcesSynchronization.saveFileToZip(archL,'arch_l.mtc','Setting Data0')
              await resourcesSynchronization.saveFileToZip(archU,'arch_o_u.mtc','Raw')
              await resourcesSynchronization.saveFileToZip(archL,'arch_o_l.mtc','Raw')
            })
          }
         
          if (upperModelCache.current !== uploadSTL.upper) {
            needResetPresetup = true
            upperModelCache.current = uploadSTL.upper
              ? uploadSTL.upper
              : null
            resourcesSynchronization.deleteFile(
              "test/case/Setting Data10/refinement/arch_u.mtc",
            )

            // save to virtual memory
            const stlFile = upperModelCache.current
            const ext = getFileExtension(stlFile) 
            let isSaveOk = false;
            if (ext === "stl") {
              const stlFilePath: Record<string, any> = {}
              stlFilePath["test/pre/arch_u.stl"] = stlFile
              await resourcesSynchronization.moduleFS(stlFilePath)
              const isSaveU = wasmModule.module.SaveMtcFileToFolder(
                "test/pre/arch_u.stl",
                "Setting Data10/refinement/arch_u.mtc",
              )
              isSaveOk= isSaveU
            }else if (ext === 'mtc') {
              await resourcesSynchronization.saveFileToZip(stlFile,'refinement/arch_u.mtc','Setting Data10')
              isSaveOk = true
            }
            console.log("isSaveUpperMTC :",isSaveOk)
          }
          if (lowerModelCache.current !== uploadSTL.lower) {
            needResetPresetup = true
            lowerModelCache.current = uploadSTL.lower
              ? uploadSTL.lower
              : null
            resourcesSynchronization.deleteFile(
              "test/case/Setting Data10/refinement/arch_l.mtc",
            )

            // save to virtual memory
            const stlFile = lowerModelCache.current
            const ext = getFileExtension(stlFile) 
            let isSaveOk = false;
            if (ext === "stl") {
              let stlFilePath: Record<string, any> = {}
              stlFilePath["test/pre/arch_l.stl"] = stlFile
              await resourcesSynchronization.moduleFS(stlFilePath)

              const isSaveU = wasmModule.module.SaveMtcFileToFolder(
                "test/pre/arch_l.stl",
                "Setting Data10/refinement/arch_l.mtc",
              )
              isSaveOk = isSaveU
            }else if (ext === 'mtc') {
              await resourcesSynchronization.saveFileToZip(stlFile,'refinement/arch_l.mtc','Setting Data10')
              isSaveOk = true
            }
            console.log("isSaveLowerMTC :",isSaveOk)
            caseManagement.getCaseFiles(["bulk10"]).then((r) => {
              const zips = []
              if (r) {
                for (const k in r) {
                  zips.push({
                    fileName: k,
                    file: r[k],
                  })
                  // for test: dowload the zip file
                  // const link = document.createElement('a')
                  // link.style.display = 'none'
                  // document.body.appendChild(link)
                  // const blob = new Blob([r[k]], { type: 'application/zip' })
                  // link.href = URL.createObjectURL(blob)
                  // link.download = k
                  // link.click()
                  // document.body.removeChild(link)
                }
                dispatch(
                  uploadCaseZips({
                    zips,
                    patientId,
                    caseId,
                  }),
                ).then(() => {
                  resolve();
                })
              }
            })
          }
        }, 100)
      })
      if (needResetPresetup) {
        setSelfplanRefineAiSuccess(false)
        requestRefinementPresetup(
          uploadSTL.upper? true: false, 
          uploadSTL.lower? true: false, 
          JSON.parse(caseDetail.udesign_json))
      }
      setStep(step + 1)
    } else if (step === 1) {
      dispatch(setRefinementReady(false))
      showJsPreview()
      treatView.Open(false)
      if (widthAll) {
        dispatch(setWidthAll(false))
      }  
      dispatch(setIsDoingPresetup(true))
      dispatch(setStageShowType(""))

      selfplanSubmit()
      dispatch(setIsCreateNewRefinement(false))
      dispatch(setIsCreateSelfplanRefinement(false))
      
      // setTimeout(() => {
      //   preview.drawSTL({
      //     upperArch: uploadSTL.upper,
      //     lowerArch: uploadSTL.lower,
      //     canvas: document.getElementById("canvas") as HTMLCanvasElement,
      //     zoomRange: [0.25, 4],
      //   })
      // }, 500)
    }
  }
  useEffect(() => {
    setSelfplanRefineAiSuccess(false)
    setIsCancelRefine(false)
    dispatch(
      setUploadStls({
        upper: null,
        lower: null,
      }),
    )
    dispatch(setPhotoTypeJson(null))
  }, [])
  return (
    <Box
      id="createselfplanrefinement"
      sx={{
        backgroundColor: "white",
        width: widthAll ? "100%" : "60%",
        height: "100%",
        position: "absolute",
        top: "0",
        right: "0",
        zIndex: "10",
        overflowY: "auto",
        paddingTop: "40px",
      }}
    >
      <Button
        variant="contained"
        sx={{
          position: "absolute",
          left: 20,
          top: 20,
          display: widthAll ? "" : "none",
        }}
        onClick={async () => {
          await dispatch(setWidthAll(!widthAll))
          window.dispatchEvent(new Event("resize"))
        }}
      >
        {widthAll ? `Show Initial` : `View New Scans and PHotos`}
      </Button>
      <IconButton
        aria-label="close"
        onClick={() => {
          if(step === 0){
            treatView.Open(false)
            dispatch(setStageShowType("normal"))
            dispatch(setIsCreateNewRefinement(false))
            dispatch(setIsCreateSelfplanRefinement(false))
            if (widthAll) {
              dispatch(setWidthAll(false))
            }
          }else if(step === 1){
            setCancelWarning(true)
          }
        }}
        sx={{
          position: "absolute",
          right: 12,
          top: 10,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
      <Box
        id="context-container"
        sx={{
          display: "flex",
          justifyContent: "center",
          minHeight: "400px",
        }}
      >
        {getCurrentStepComponent()}
      </Box>
      <Box
        id="navigator"
        sx={{
          position: "relative",
          margin: "40px",
          display: "flex",
          justifyContent: step === 0 ? "end" : "space-between",
        }}
      >
        {step !== 0 && (
          <Button
            variant={"outlined"}
            onClick={() => {
              setStep(step - 1 > -1 ? step - 1 : 0)
            }}
            sx={{ width: "92px", height: "36px" }}
          >
            <ChevronLeftIcon fontSize={"small"} sx={{ marginLeft: "3px" }} />
            {t("button.back")}
          </Button>
        )}
        <Button
          onClick={() => {
            changeToNextStep()
          }}
          variant="contained"
          sx={{
            right: "10px",
            width: "92px",
            height: "36px",
          }}
        >
          {step === 0? t("button.next") : t("button.submit")}
          <ChevronRightIcon sx={{ marginLeft: "3px" }} fontSize={"small"} />
        </Button>
      </Box>
      <DialogBox open={cancelWarning} title="Warning" content={<>{"Are you sure you want to exit the process?"}</>} actions=
      {<>
          <UButton
            onClickHandler={ () => {
              setCancelWarning(false)
            }}
            variant={"text"}
            btnText={("cancel")}
          />
          <UButton
            onClickHandler={ () => {
              setIsCancelRefine(true)
              cancelRefine()
              handleCancel()
            }}
            variant={"text"}
            btnText={("Confirm")}
          />
        </>
      }
      >
      </DialogBox>
      <UAlert
        horizontalPosition={"right"}
        timeout={timeout}
        alertContent={
          <>
            <UText
              sxProp={{
                fontSize: "14px",
                fontStyle: "normal",
                fontWeight: 500,
                lineHeight: "143%",
              }}
            >
              {alertMsg}
            </UText>
            {!!btnText && !!btnAction && (
              <UButton
                sxProp={{
                  width: "120px",
                  height: "30px",
                  color: "#ffffff",
                  border: "1px solid #ffffff",
                  borderRadius: "4px",
                  fontSize: "13px",
                  marginLeft: 2,
                  padding: "4px 10px",
                }}
                onClickHandler={btnAction}
                btnText={btnText}
              />
            )}
          </>
        }
        variant={"filled"}
        severity={isError ? "error" : "success"}
        alertProps={{ paddingBottom: "10px" }}
        showAlert={alertMsg !== ""}
        onClickHandler={(action) => {
          if (action === 'clickaway') {
            return;
          }
          dispatch(closeAlert());
        }}
      ></UAlert>
    </Box>
  )
}